import React, { useState, useCallback } from 'react';
import Icon from '../../../components/AppIcon';


const STATUS_CONFIG = {
  pending: { label: 'Pending', color: 'text-amber-400', bg: 'bg-amber-500/10', dot: 'bg-amber-400', pulse: true },
  processing: { label: 'Processing', color: 'text-blue-400', bg: 'bg-blue-500/10', dot: 'bg-blue-400', pulse: true },
  paid: { label: 'Paid', color: 'text-emerald-400', bg: 'bg-emerald-500/10', dot: 'bg-emerald-400', pulse: false },
  failed: { label: 'Failed', color: 'text-red-400', bg: 'bg-red-500/10', dot: 'bg-red-400', pulse: false },
  canceled: { label: 'Canceled', color: 'text-slate-400', bg: 'bg-slate-500/10', dot: 'bg-slate-400', pulse: false },
};

const StripePayoutHistory = ({ payouts, loading, onSyncStripe, syncing }) => {
  const totalPaid = payouts?.filter(p => p?.status === 'paid')?.reduce((s, p) => s + parseFloat(p?.amount || 0), 0) || 0;
  const totalPending = payouts?.filter(p => ['pending', 'processing']?.includes(p?.status))?.reduce((s, p) => s + parseFloat(p?.amount || 0), 0) || 0;

  const formatDate = (dateStr) => {
    if (!dateStr) return '—';
    try {
      return new Date(dateStr)?.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
    } catch { return '—'; }
  };

  return (
    <div className="bg-card border border-border rounded-xl overflow-hidden">
      {/* Header */}
      <div className="px-5 py-4 border-b border-border flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="w-9 h-9 rounded-lg bg-violet-500/15 flex items-center justify-center">
            <Icon name="CreditCard" size={18} className="text-violet-400" />
          </div>
          <div>
            <h3 className="text-sm font-semibold text-foreground font-heading">Stripe Payout History</h3>
            <p className="text-xs text-muted-foreground">Transaction details & processing status</p>
          </div>
        </div>
        <button
          onClick={onSyncStripe}
          disabled={syncing}
          className="flex items-center gap-1.5 text-xs text-primary hover:text-primary/80 font-medium transition-colors disabled:opacity-50"
        >
          <Icon name="RefreshCw" size={13} className={syncing ? 'animate-spin' : ''} />
          Sync
        </button>
      </div>

      {/* Summary */}
      <div className="grid grid-cols-2 divide-x divide-border border-b border-border">
        <div className="px-5 py-3 text-center">
          <p className="text-lg font-bold text-emerald-400 data-text">${totalPaid?.toFixed(2)}</p>
          <p className="text-xs text-muted-foreground">Total Paid Out</p>
        </div>
        <div className="px-5 py-3 text-center">
          <p className="text-lg font-bold text-amber-400 data-text">${totalPending?.toFixed(2)}</p>
          <p className="text-xs text-muted-foreground">Pending Payouts</p>
        </div>
      </div>

      {/* Payout List */}
      <div className="p-4">
        {loading ? (
          <div className="py-8 flex items-center justify-center">
            <div className="w-6 h-6 border-2 border-primary border-t-transparent rounded-full animate-spin" />
          </div>
        ) : payouts?.length === 0 ? (
          <div className="py-8 text-center">
            <Icon name="CreditCard" size={28} className="text-muted-foreground mx-auto mb-2" />
            <p className="text-sm text-muted-foreground">No payout history yet</p>
            <p className="text-xs text-muted-foreground mt-1">Payouts appear once earnings are approved</p>
          </div>
        ) : (
          <div className="space-y-2 max-h-64 overflow-y-auto pr-1">
            {payouts?.map((payout, i) => {
              const cfg = STATUS_CONFIG?.[payout?.status] || STATUS_CONFIG?.pending;
              return (
                <div key={payout?.id || i} className="flex items-center justify-between p-3 rounded-lg bg-muted/30 hover:bg-muted/50 transition-colors">
                  <div className="flex items-center gap-3 min-w-0">
                    <div className={`w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0 ${cfg?.bg}`}>
                      <span className={`w-2 h-2 rounded-full ${cfg?.dot} ${cfg?.pulse ? 'animate-pulse' : ''}`} />
                    </div>
                    <div className="min-w-0">
                      <p className="text-xs font-medium text-foreground truncate">
                        {payout?.description || `Payout #${(i + 1)?.toString()?.padStart(3, '0')}`}
                      </p>
                      <p className="text-xs text-muted-foreground">
                        {formatDate(payout?.created_at)}
                        {payout?.arrival_date && ` · Arrives ${formatDate(payout?.arrival_date)}`}
                      </p>
                      {payout?.stripe_payout_id && (
                        <p className="text-xs text-muted-foreground font-mono truncate">{payout?.stripe_payout_id?.slice(0, 20)}...</p>
                      )}
                    </div>
                  </div>
                  <div className="text-right flex-shrink-0 ml-3">
                    <p className="text-sm font-bold text-foreground data-text">${parseFloat(payout?.amount || 0)?.toFixed(2)}</p>
                    <span className={`text-xs font-medium ${cfg?.color}`}>{cfg?.label}</span>
                    {payout?.failure_message && (
                      <p className="text-xs text-red-400 mt-0.5 max-w-[120px] truncate">{payout?.failure_message}</p>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
};

export default StripePayoutHistory;
